/* eslint-disable react/prop-types */
import React, { useState, memo } from 'react';
import {
  View,
  Modal,
  TouchableOpacity,
  Dimensions,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { QText } from './index';
import { useTheme } from 'react-native-paper';
import { makeStyles } from '../hooks/MakeStylesHook';
import CloseIcon from './Icons/CloseIcon';
import UniversalDocumentViewer from './UniversalDocumentViewer';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

const useStyles = makeStyles((theme) => ({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  header: {
    position: 'absolute',
    top: 50,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    zIndex: 2,
  },
  title: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
    flex: 1,
    marginRight: 15,
  },
  closeButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 20,
    padding: 8,
  },
  contentContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  imageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  pdfContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  documentContainer: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 300,
    maxWidth: screenWidth - 40,
  },
  documentIcon: {
    marginBottom: 20,
  },
  documentTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.secondaryText,
    textAlign: 'center',
    marginBottom: 10,
  },
  documentInfo: {
    fontSize: 14,
    color: theme.colors.placeHolderText,
    textAlign: 'center',
    marginBottom: 20,
  },
  actionButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 10,
  },
  actionButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '500',
  },
  footer: {
    position: 'absolute',
    bottom: 50,
    left: 20,
    right: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 12,
    padding: 15,
  },
  footerText: {
    color: 'white',
    fontSize: 14,
    textAlign: 'center',
    opacity: 0.9,
  },
  downloadButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  downloadButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '500',
  },
}));

const UniversalFileViewer = ({ visible, onClose, attachment }) => {
  const styles = useStyles();
  const theme = useTheme();
  const [isOpening, setIsOpening] = useState(false);

  if (!visible || !attachment) return null;

  const fileName = attachment.name || 'File';
  const fileType = attachment.type || '';
  const attachmentId = attachment.id;

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'Size unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleOpenFile = async () => {
    if (isOpening) return;

    try {
      setIsOpening(true);

      if (attachment.uri) {
        if (Platform.OS === 'web') {
          // For web, create a download link
          const link = document.createElement('a');
          link.href = attachment.uri;
          link.download = attachment.name || 'file';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          // For mobile, use the SecureFileOpener
          const event = new CustomEvent('openSecureFile', {
            detail: { attachmentId, uri: attachment.uri },
          });
          window.dispatchEvent(event);
        }
      } else {
        const event = new CustomEvent('openSecureFile', {
          detail: { attachmentId },
        });
        window.dispatchEvent(event);
      }
    } catch (error) {
      console.error('Error opening file:', error);
      Alert.alert('Error', 'Failed to open the file.');
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent={true}
    >
      <View style={styles.overlay}>
        {/* Header */}
        <View style={styles.header}>
          <QText style={styles.title} numberOfLines={2}>
            {fileName}
          </QText>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <CloseIcon width={24} height={24} color="white" />
          </TouchableOpacity>
        </View>

        {/* Content */}
        <View style={styles.container}>
          {attachment.uri ? (
            <UniversalDocumentViewer
              uri={attachment.uri}
              fileType={attachment.type}
              fileName={attachment.name}
              style={{
                width: screenWidth - 40,
                height: screenHeight - 140,
                borderRadius: 8,
              }}
              onError={(error) => {
                console.error('Error loading document from URI:', error);
              }}
            />
          ) : (
            <UniversalDocumentViewer
              attachmentId={attachmentId}
              fileType={attachment.type}
              fileName={attachment.name}
              style={{
                width: screenWidth - 40,
                height: screenHeight - 140,
                borderRadius: 8,
              }}
              onError={(error) => {
                console.error(
                  'Error loading document from attachmentId:',
                  error
                );
              }}
            />
          )}
        </View>

        <View style={styles.footer}>
          <QText style={styles.footerText}>
            {attachment.size && attachment.size > 0
              ? `${formatFileSize(attachment.size)} • ${fileType || 'Unknown file type'}`
              : fileType || 'Unknown file type'}
          </QText>
        </View>
      </View>
    </Modal>
  );
};

export default memo(UniversalFileViewer);
